import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import usePrivateLink from "../config/usePrivateLink";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import {
  familyStudentList,
  familyFeeDetails,
  familyFeeDeposit,
  studentFeeReceiptNo,
} from "../../api/Student";
import { BankList } from "../../api/BankService";
import { feeTypes } from "../config/Data";
import Master from "../studentFeeReport/Master";

const VIEW_KEY = "family_fee_view"; // search + selected family (survives refresh)
const RECENT_KEY = "family_fee_recent"; // last families used
const draftKey = (f) => `family_fee_draft:${f.father_name}|${f.phone}`;

const PRIORITY = [
  "Previous Session Fees",
  ...feeTypes,
  "Library Fee",
  "Transport Fee",
  "Hostel Fee",
];
const rank = (n) => {
  const i = PRIORITY.indexOf(n);
  return i === -1 ? 999 : i;
};

const num = (v) => Number(v) || 0;
const int = (v) => Math.max(0, Math.ceil(num(v)));
const inr = (v) => num(v).toLocaleString("en-IN");
const pad = (n) => String(n).padStart(2, "0");
const dateISO = (addMonths = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + addMonths);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const initials = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "F";
  if (w.length === 1) return w[0].slice(0, 2).toUpperCase();
  return (w[0][0] + w[w.length - 1][0]).toUpperCase();
};

const errText = (e) => {
  const m = e?.response?.data?.message ?? e?.message ?? "Something went wrong";
  return Array.isArray(m) ? m.join(", ") : m;
};

const readJSON = (storage, key, fallback) => {
  try {
    const v = storage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};
const writeJSON = (storage, key, value) => {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / blocked - ignore */
  }
};

// "RCPT-1511" -> { prefix: "RCPT-", number: "1511" }
const splitReceiptNo = (value) => {
  const m = String(value ?? "").match(/^(\D*)(\d+)$/);
  return m
    ? { prefix: m[1], number: m[2] }
    : { prefix: "", number: value ?? "" };
};
const addToNumber = (numberStr, k) =>
  /^\d+$/.test(String(numberStr))
    ? String(Number(numberStr) + k).padStart(String(numberStr).length, "0")
    : numberStr;

// Highlights the searched text inside a result
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const Hi = ({ text, q }) => {
  const t = String(text ?? "");
  const w = (q || "").trim();
  if (w.length < 2) return t;
  return t.split(new RegExp(`(${escapeRe(w)})`, "gi")).map((p, i) =>
    p.toLowerCase() === w.toLowerCase() ? (
      <mark key={i} className="px-0 bg-label-warning">
        {p}
      </mark>
    ) : (
      p
    ),
  );
};

const SectionTitle = ({ no, title, hint }) => (
  <div className="d-flex align-items-center gap-3 mb-3">
    <span
      className="d-flex align-items-center justify-content-center rounded-circle bg-primary text-white fw-bold"
      style={{ width: 30, height: 30, flexShrink: 0 }}
    >
      {no}
    </span>
    <div>
      <h6 className="mb-0 fw-bold">{title}</h6>
      <small className="text-muted">{hint}</small>
    </div>
  </div>
);

/* ============================================================
   FEE LOGIC (same rules as the single-student page)
============================================================ */

// Group a child's fees by fee type. batchId filters batch fees (like the single page)
const buildFeeData = (s, batchId = "") => {
  const map = {};

  const add = (name, ids, total, paid, pending, batch = []) => {
    if (!map[name]) {
      map[name] = {
        name,
        ids: [],
        batch_id: [],
        total: 0,
        paid: 0,
        pending: 0,
        selected: false,
        payAmount: 0,
      };
    }
    const m = map[name];
    m.ids.push(...ids);
    m.batch_id.push(...batch);
    m.total += total;
    m.paid += paid;
    m.pending += pending;
  };

  (s.batch_n_fees || [])
    .filter((b) => !batchId || Number(b.batch_id) === Number(batchId))
    .forEach((b) =>
      (b.student_fee_details || []).forEach((f) =>
        add(
          f.fee_type,
          [b.id],
          int(f.final_amount),
          int(f.paid_amount),
          int(f.due_amount),
          [b.batch_id],
        ),
      ),
    );

  const moduleFee = (name, list) => {
    if (!list?.length) return;
    let total = 0;
    let paid = 0;
    list.forEach((i) => {
      total += int(i.final_amount);
      paid += Math.floor(num(i.paid_amount));
    });
    add(
      name,
      list.map((i) => i.id),
      total,
      paid,
      Math.max(0, total - paid),
    );
  };
  moduleFee("Transport Fee", s.bus_student_fees);
  moduleFee("Hostel Fee", s.hostel_student_fees);
  moduleFee("Library Fee", s.library_student_fees);

  return Object.values(map)
    .filter((f) => f.total > 0)
    .map((f) => ({ ...f, selected: f.pending > 0 }))
    .sort((a, b) => rank(a.name) - rank(b.name));
};

const sum = (arr, fn) => arr.reduce((a, x) => a + fn(x), 0);

// Recalculate a child's totals from its fee list
const refresh = (s, feeData) => ({
  ...s,
  feeData,
  total: sum(feeData, (f) => f.total),
  paid: sum(feeData, (f) => f.paid),
  pending: sum(feeData, (f) => f.pending),
  payAmount: sum(feeData, (f) => (f.selected ? f.payAmount : 0)),
});

const makeStudent = (raw, batchId = "") =>
  refresh({ ...raw, batchId, open: false }, buildFeeData(raw, batchId));

// How much can be paid on the ticked fees (without Hostel extra)
const autoCap = (s) => sum(s.feeData, (f) => (f.selected ? f.pending : 0));

// Put an amount on the ticked fees in priority order.
// extra=true lets a ticked Hostel Fee take more than pending (advance), like the single page.
const applyAmount = (s, amount, extra = false) => {
  const hostelOn =
    extra && s.feeData.some((f) => f.selected && f.name === "Hostel Fee");
  let rem = hostelOn ? int(amount) : Math.min(int(amount), autoCap(s));
  const feeData = s.feeData.map((f) => {
    if (!f.selected) return { ...f, payAmount: 0 };
    const pay =
      f.name === "Hostel Fee" && hostelOn ? rem : Math.min(f.pending, rem);
    rem -= pay;
    return { ...f, payAmount: pay };
  });
  return refresh(s, feeData);
};

// Divide money equally; nobody gets more than their cap
const equalSplit = (total, caps) => {
  const out = caps.map(() => 0);
  let rem = total;
  let active = caps.map((c, i) => i).filter((i) => caps[i] > 0);
  while (rem > 0 && active.length) {
    const share = Math.floor(rem / active.length);
    if (share === 0) {
      for (const i of active) {
        if (rem <= 0) break;
        out[i] += 1;
        rem -= 1;
      }
      break;
    }
    active.forEach((i) => {
      const give = Math.min(share, caps[i] - out[i]);
      out[i] += give;
      rem -= give;
    });
    active = active.filter((i) => out[i] < caps[i]);
  }
  return out;
};

/* ============================================================
   PAGE
============================================================ */

export default function FamilyFeeDeposit() {
  usePrivateLink();
  const navigate = useNavigate();
  const [, setParams] = useSearchParams();

  const [loading, setLoading] = useState(false);

  // search
  const [search, setSearch] = useState("");
  const [families, setFamilies] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [recent, setRecent] = useState(() =>
    readJSON(localStorage, RECENT_KEY, []),
  );
  const searchBox = useRef(null);
  const focused = useRef(false);
  const reqId = useRef(0);

  // family + students
  const [family, setFamily] = useState(null);
  const [students, setStudents] = useState([]);
  const [received, setReceived] = useState("");
  const [splitMode, setSplitMode] = useState("sequential"); // sequential | equal | manual

  // payment details
  const [banks, setBanks] = useState([]);
  const [setting, setSetting] = useState({});
  const [form, setForm] = useState({
    bank_id: "",
    transaction_no: "",
    payment_date: dateISO(0),
    next_payment_date: dateISO(1),
    remark: "",
    sendMessage: true,
  });

  // receipts
  const [receiptStart, setReceiptStart] = useState({
    prefix: "RCPT-",
    number: "",
  });
  const [receiptMsg, setReceiptMsg] = useState("");
  const [result, setResult] = useState(null);

  /* ---------- keep search + family in URL and sessionStorage ---------- */
  const view = useRef({ q: "", father: "", phone: "" });
  const updateView = (patch) => {
    view.current = { ...view.current, ...patch };
    const v = view.current;
    const p = new URLSearchParams();
    if (v.q) p.set("q", v.q);
    if (v.father) p.set("father", v.father);
    if (v.phone) p.set("phone", v.phone);
    setParams(p, { replace: true });
    writeJSON(sessionStorage, VIEW_KEY, v.q || v.father ? v : null);
  };

  /* ---------- first load ---------- */
  useEffect(() => {
    setSetting(readJSON(localStorage, "setting", {}));

    (async () => {
      try {
        const [bankRes, rcptRes] = await Promise.all([
          BankList(),
          studentFeeReceiptNo("receipt_no=RCPT-1"),
        ]);
        if (bankRes?.data?.status) setBanks(bankRes.data.data);
        if (rcptRes?.data?.status)
          setReceiptStart(splitReceiptNo(rcptRes.data.data?.next_receipt_no));
      } catch (e) {
        SweetAlertShow("error", "Error", errText(e));
      }
    })();

    // restore what the user was doing before refresh
    const url = new URLSearchParams(window.location.search);
    const saved = readJSON(sessionStorage, VIEW_KEY, {});
    const v = {
      q: url.get("q") ?? saved.q ?? "",
      father: url.get("father") ?? saved.father ?? "",
      phone: url.get("phone") ?? saved.phone ?? "",
    };
    view.current = v;
    if (v.q) setSearch(v.q);
    if (v.father && v.phone)
      pickFamily({ father_name: v.father, phone: v.phone });
    else updateView({});
    // eslint-disable-next-line
  }, []);

  /* ---------- close dropdown on outside click ---------- */
  useEffect(() => {
    const close = (e) => {
      if (searchBox.current && !searchBox.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  /* ---------- search (debounced, ignores old responses) ---------- */
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) {
      setFamilies([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const my = ++reqId.current;
    const t = setTimeout(async () => {
      try {
        const res = await familyStudentList(`search=${encodeURIComponent(q)}`);
        if (my !== reqId.current) return;
        const d = res?.data?.data;
        setFamilies(Array.isArray(d) ? d : d?.data || []);
        setActiveIdx(0);
        if (focused.current) setShowDropdown(true);
      } catch (e) {
        if (my !== reqId.current) return;
        setFamilies([]);
        SweetAlertShow("error", "Error", errText(e));
      } finally {
        if (my === reqId.current) setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const onSearchChange = (value) => {
    setSearch(value);
    setShowDropdown(true);
    updateView({ q: value });
  };

  const list = search.trim().length >= 2 ? families : recent;

  const onSearchKey = (e) => {
    if (e.key === "Escape") return setShowDropdown(false);
    if (!showDropdown || !list.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (list[activeIdx]) pickFamily(list[activeIdx]);
    }
  };

  /* ---------- verify first receipt number ---------- */
  const startReceiptNo = `${receiptStart.prefix}${receiptStart.number}`;
  useEffect(() => {
    if (!receiptStart.number) return;
    const t = setTimeout(async () => {
      try {
        const res = await studentFeeReceiptNo(`receipt_no=${startReceiptNo}`);
        setReceiptMsg(res?.data?.data?.message || "");
      } catch {
        setReceiptMsg("");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [startReceiptNo]);

  /* ---------- open a family ---------- */
  const pickFamily = async (fam) => {
    const father = fam?.father_name || "";
    const phone =
      fam?.phone ||
      fam?.mobile ||
      fam?.students?.[0]?.phone ||
      fam?.students?.[0]?.mobile ||
      "";

    if (!father || !phone) {
      SweetAlertShow(
        "error",
        "Error",
        "Father name or mobile number is missing for this family.",
      );
      return;
    }

    setShowDropdown(false);
    setFamily({ father_name: father, phone });
    setStudents([]);
    setResult(null);
    setLoading(true);
    updateView({ father, phone });

    try {
      const username =
        localStorage.getItem("username") ||
        localStorage.getItem("user_name") ||
        "brschool";
      const userType = localStorage.getItem("user_type") || "teacher";

      const res = await familyFeeDetails(
        `username=${encodeURIComponent(username)}&user_type=${encodeURIComponent(userType)}` +
          `&father_name=${encodeURIComponent(father)}&phone=${encodeURIComponent(phone)}`,
      );

      if (!res?.data?.status) {
        SweetAlertShow(
          "error",
          "Error",
          res?.data?.message || "Could not load fee details.",
        );
        return;
      }

      const d = res.data.data;
      const fresh = (d.students || []).map((raw) => makeStudent(raw));
      const draft = readJSON(
        sessionStorage,
        draftKey({ father_name: father, phone }),
        null,
      );

      setFamily({ father_name: father, phone, mother_name: d.mother_name });

      if (draft) {
        // put back what was typed before the refresh
        setReceived(draft.received ?? "");
        setSplitMode(draft.splitMode || "manual");
        setForm((f) => ({ ...f, ...draft.form }));
        setStudents(
          fresh.map((s) => {
            const sv = (draft.students || []).find(
              (x) => x.id === s.student_id,
            );
            if (!sv) return s;
            const base = makeStudent(s, sv.batchId || "");
            const feeData = base.feeData.map((f) => {
              const x = (sv.fees || []).find((y) => y.name === f.name);
              if (!x) return f;
              const max = f.name === "Hostel Fee" ? Infinity : f.pending;
              return {
                ...f,
                selected: !!x.selected,
                payAmount: Math.min(int(x.payAmount), max),
              };
            });
            return { ...refresh(base, feeData), open: !!sv.open };
          }),
        );
      } else {
        setReceived("");
        setSplitMode("sequential");
        setStudents(fresh);
      }

      // remember in "recent families"
      const entry = {
        father_name: father,
        phone,
        total_students: fresh.length,
        students: fresh.map((s) => ({
          name: s.name,
          registration_no: s.registration_no,
          phone,
        })),
      };
      const next = [
        entry,
        ...readJSON(localStorage, RECENT_KEY, []).filter(
          (r) => !(r.father_name === father && r.phone === phone),
        ),
      ].slice(0, 5);
      writeJSON(localStorage, RECENT_KEY, next);
      setRecent(next);
    } catch (e) {
      SweetAlertShow("error", "Error", errText(e));
    } finally {
      setLoading(false);
    }
  };

  const closeFamily = (clearSearch = false) => {
    setFamily(null);
    setStudents([]);
    setReceived("");
    setResult(null);
    if (clearSearch) setSearch("");
    updateView({ father: "", phone: "", ...(clearSearch ? { q: "" } : {}) });
  };

  /* ---------- keep the draft so refresh does not lose it ---------- */
  useEffect(() => {
    if (!family || !students.length || result) return;
    writeJSON(sessionStorage, draftKey(family), {
      received,
      splitMode,
      form,
      students: students.map((s) => ({
        id: s.student_id,
        batchId: s.batchId,
        open: s.open,
        fees: s.feeData.map((f) => ({
          name: f.name,
          selected: f.selected,
          payAmount: f.payAmount,
        })),
      })),
    });
  }, [students, received, splitMode, form, family, result]);

  /* ---------- auto divide when total changes (unless "I will enter") ---------- */
  const pendKey = students.map((s) => `${s.student_id}:${s.pending}`).join(",");
  useEffect(() => {
    if (!students.length || splitMode === "manual") return;
    const total = int(received);
    setStudents((prev) => {
      const caps = prev.map(autoCap);
      let parts;
      if (splitMode === "equal") {
        parts = equalSplit(total, caps);
      } else {
        let rem = total;
        parts = caps.map((c) => {
          const p = Math.min(c, rem);
          rem -= p;
          return p;
        });
      }
      return prev.map((s, i) => applyAmount(s, parts[i], false));
    });
    // eslint-disable-next-line
  }, [received, splitMode, pendKey]);

  /* ---------- actions on one child ---------- */
  const edit = (idx, fn) =>
    setStudents((prev) => prev.map((s, i) => (i === idx ? fn(s) : s)));

  const setStudentAmount = (idx, value) => {
    setSplitMode("manual");
    edit(idx, (s) => applyAmount(s, value, true));
  };

  const payFull = (idx) => {
    setSplitMode("manual");
    edit(idx, (s) => applyAmount(s, autoCap(s), false));
  };

  const toggleOpen = (idx) => edit(idx, (s) => ({ ...s, open: !s.open }));

  // Same rules as the single page: Tuition pulls in Registration; Registration cannot be
  // unticked while Tuition is ticked.
  const toggleFee = (idx, name) => {
    setSplitMode("manual");
    edit(idx, (s) => {
      const feeData = s.feeData.map((f) => ({ ...f }));
      const fee = feeData.find((f) => f.name === name);
      const next = !fee.selected;

      if (
        name === "Registration Fee" &&
        !next &&
        feeData.find((f) => f.name === "Tuition Fee")?.selected
      ) {
        return s;
      }
      fee.selected = next;
      if (name === "Tuition Fee" && next) {
        const reg = feeData.find((f) => f.name === "Registration Fee");
        if (reg) reg.selected = true;
      }
      // keep the child's amount, spread it over the ticked fees
      return applyAmount(refresh(s, feeData), s.payAmount, true);
    });
  };

  const setFeeAmount = (idx, name, value) => {
    if (name === "Registration Fee") return; // locked, like the single page
    setSplitMode("manual");
    edit(idx, (s) =>
      refresh(
        s,
        s.feeData.map((f) => {
          if (f.name !== name) return f;
          let a = int(value);
          if (name !== "Hostel Fee") a = Math.min(a, f.pending);
          return { ...f, payAmount: a };
        }),
      ),
    );
  };

  const changeBatch = (idx, batchId) =>
    edit(idx, (s) => {
      const moved = refresh({ ...s, batchId }, buildFeeData(s, batchId));
      return applyAmount(moved, s.payAmount, true);
    });

  const clearAll = () => {
    setSplitMode("manual");
    setStudents((prev) => prev.map((s) => applyAmount(s, 0)));
  };

  const onReceivedChange = (v) => {
    setReceived(v);
    // typing a new total with nothing entered by hand -> divide automatically again
    if (splitMode === "manual" && allocated === 0) setSplitMode("sequential");
  };

  /* ---------- totals ---------- */
  const totalPending = useMemo(
    () => sum(students, (s) => s.pending),
    [students],
  );
  const allocated = useMemo(
    () => sum(students, (s) => s.payAmount),
    [students],
  );
  const receivedAmt = int(received);
  const remaining = receivedAmt - allocated;

  const receipts = useMemo(
    () =>
      students
        .filter((s) => s.payAmount > 0)
        .map((s, k) => ({
          ...s,
          receipt_no: `${receiptStart.prefix}${addToNumber(receiptStart.number, k)}`,
        })),
    [students, receiptStart],
  );

  const blocker = !receivedAmt
    ? "Enter the amount received from parent."
    : remaining > 0
      ? `₹${inr(remaining)} is still not divided.`
      : remaining < 0
        ? `Divided amount is ₹${inr(-remaining)} more than received.`
        : !form.bank_id
          ? "Select a bank."
          : !receiptStart.number
            ? "Enter the receipt number."
            : "";

  /* ---------- save ---------- */
  const handleSave = async () => {
    if (blocker) return;

    const ok = window.confirm(
      `Save ₹${inr(receivedAmt)} for ${family?.father_name}?\n\n` +
        receipts
          .map((r) => `${r.receipt_no}  ${r.name}  ₹${inr(r.payAmount)}`)
          .join("\n"),
    );

    if (!ok) return;

    setLoading(true);

    try {
      const res = await familyFeeDeposit({
        total_amount: receivedAmt,
        bank_id: form.bank_id,
        transaction_no: form.transaction_no,
        payment_date: form.payment_date,
        next_payment_date: form.next_payment_date,
        remark: form.remark,
        sendMessage: form.sendMessage ? 1 : 0,

        students: receipts.map((r) => ({
          student_id: r.student_id,
          receipt_no: r.receipt_no,
          amount: r.payAmount,

          modules: r.feeData
            .filter((f) => f.selected && Number(f.payAmount) > 0)
            .map((f) => ({
              type: f.name,
              ids: f.ids,
              amount: f.payAmount,
            })),
        })),
      });

      if (res?.data?.status) {
        // Saved successfully - remove draft
        writeJSON(sessionStorage, draftKey(family), null);

        SweetAlertShow("success", "Successfully", res.data.message);

        const done = res.data.data?.receipts || [];

        if (done.length === 1) {
          navigate(`/student/fees/receipt?id=${done[0].id}`);
        } else {
          setResult(done);
        }
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          res?.data?.message || "Payment could not be saved.",
        );
      }
    } catch (e) {
      SweetAlertShow("error", "Error", errText(e));
    } finally {
      setLoading(false);
    }
  };

  const setF = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const [more, setMore] = useState(false);
  const rno = Object.fromEntries(
    receipts.map((r) => [r.student_id, r.receipt_no]),
  );

  /* ============================================================
       RENDER
    ============================================================ */

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid mt-4">
                <Master />
                <div className="col-12">
                  <div className="card p-3 mb-3 shadow-sm border-0 rounded-3">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="bg-primary bg-gradient text-white rounded-3 d-flex align-items-center justify-content-center"
                        style={{ width: 45, height: 45, minWidth: 45 }}
                      >
                        <i className="ri-group-line fs-5"></i>
                      </div>

                      <div>
                        <h5 className="m-0 fw-bold">Family Fee Deposit</h5>
                        <small className="text-muted">
                          Manage family payments and student receipts
                        </small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ---------- SUCCESS ---------- */}
                {result && (
                  <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                      <i
                        className="ri-checkbox-circle-line text-success"
                        style={{ fontSize: 56 }}
                      />
                      <h5 className="mt-2">Payment saved</h5>
                      <p className="text-muted">
                        {result.length} receipts were created.
                      </p>
                      <div
                        className="d-flex flex-column gap-2 mx-auto"
                        style={{ maxWidth: 460 }}
                      >
                        {result.map((r) => (
                          <div
                            key={r.id}
                            className="d-flex justify-content-between align-items-center border rounded-3 p-3"
                          >
                            <div className="text-start">
                              <div className="fw-semibold">{r.name}</div>
                              <small className="text-muted">
                                {r.receipt_no} · ₹{inr(r.amount)}
                              </small>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() =>
                                navigate(`/student/fees/receipt?id=${r.id}`)
                              }
                            >
                              View receipt
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        className="btn btn-primary mt-4"
                        onClick={() => closeFamily(true)}
                      >
                        Take another payment
                      </button>
                    </div>
                  </div>
                )}

                {!result && (
                  <>
                    {/* ======================================================
                        STEP 1 - FIND FAMILY
                    ====================================================== */}
                    <div
                      className="card border-0 shadow-sm mb-3"
                      style={{ position: "relative", zIndex: 20 }}
                    >
                      <div className="card-body">
                        <SectionTitle
                          no={1}
                          title="Find the family"
                          hint="Search by father name, child name, registration no. or mobile"
                        />

                        {!family ? (
                          <div ref={searchBox} style={{ position: "relative" }}>
                            <div className="input-group input-group-merge">
                              <span className="input-group-text">
                                <i className="ri-search-2-line" />
                              </span>
                              <input
                                className="form-control"
                                placeholder="Type at least 2 letters…  (use ↑ ↓ and Enter to pick)"
                                value={search}
                                autoComplete="off"
                                onChange={(e) => onSearchChange(e.target.value)}
                                onKeyDown={onSearchKey}
                                onFocus={() => {
                                  focused.current = true;
                                  if (list.length) setShowDropdown(true);
                                }}
                                onBlur={() => (focused.current = false)}
                              />
                              {searching && (
                                <span className="input-group-text">
                                  <span className="spinner-border spinner-border-sm text-primary" />
                                </span>
                              )}
                              {search && !searching && (
                                <button
                                  type="button"
                                  className="input-group-text bg-transparent"
                                  title="Clear search"
                                  onClick={() => {
                                    onSearchChange("");
                                    setShowDropdown(false);
                                  }}
                                >
                                  <i className="ri-close-line" />
                                </button>
                              )}
                            </div>

                            {search.trim().length === 1 && (
                              <small className="text-warning d-block mt-1">
                                Type at least 2 characters.
                              </small>
                            )}

                            {showDropdown &&
                              (search.trim().length >= 2 ||
                                recent.length > 0) && (
                                <div
                                  className="bg-white border rounded-3 shadow"
                                  style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    marginTop: 6,
                                    maxHeight: 380,
                                    overflowY: "auto",
                                    zIndex: 1000,
                                  }}
                                >
                                  <div
                                    className="px-3 py-2 border-bottom small fw-semibold text-muted"
                                    style={{ background: "#f8f9fa" }}
                                  >
                                    {search.trim().length >= 2
                                      ? `${families.length} ${families.length === 1 ? "family" : "families"} found`
                                      : "Recently used"}
                                  </div>

                                  {list.length === 0 ? (
                                    <div className="p-4 text-center text-muted">
                                      {searching
                                        ? "Searching…"
                                        : "No family found. Try a different name or number."}
                                    </div>
                                  ) : (
                                    list.map((f, i) => {
                                      const mobile =
                                        f.phone ||
                                        f.mobile ||
                                        f.students?.[0]?.phone ||
                                        "-";
                                      const count =
                                        f.total_students ??
                                        f.students?.length ??
                                        0;
                                      return (
                                        <div
                                          key={`${f.father_name}-${mobile}-${i}`}
                                          className="d-flex align-items-start gap-3 p-3 border-bottom"
                                          style={{
                                            cursor: "pointer",
                                            background:
                                              i === activeIdx ? "#f3f3ff" : "",
                                          }}
                                          onMouseEnter={() => setActiveIdx(i)}
                                          onMouseDown={(e) =>
                                            e.preventDefault()
                                          }
                                          onClick={() => pickFamily(f)}
                                        >
                                          <div
                                            className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary fw-bold"
                                            style={{
                                              width: 40,
                                              height: 40,
                                              flexShrink: 0,
                                            }}
                                          >
                                            {initials(f.father_name)}
                                          </div>
                                          <div
                                            className="flex-grow-1"
                                            style={{ minWidth: 0 }}
                                          >
                                            <div className="fw-semibold">
                                              <Hi
                                                text={f.father_name}
                                                q={search}
                                              />
                                            </div>
                                            <small className="text-muted">
                                              {count}{" "}
                                              {count === 1
                                                ? "child"
                                                : "children"}{" "}
                                              · <Hi text={mobile} q={search} />
                                            </small>
                                            <div className="d-flex flex-wrap gap-1 mt-1">
                                              {(f.students || []).map(
                                                (s, k) => (
                                                  <span
                                                    key={k}
                                                    className="badge bg-label-secondary fw-normal"
                                                  >
                                                    <Hi
                                                      text={s.name}
                                                      q={search}
                                                    />{" "}
                                                    (
                                                    {
                                                      <Hi
                                                        text={s.registration_no}
                                                        q={search}
                                                      />
                                                    }
                                                    )
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              )}
                          </div>
                        ) : (
                          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary fw-bold"
                                style={{ width: 48, height: 48 }}
                              >
                                {initials(family.father_name)}
                              </div>
                              <div>
                                <div className="fw-bold fs-5">
                                  {family.father_name}
                                </div>
                                <small className="text-muted">
                                  Mother: {family.mother_name || "-"} · Mobile:{" "}
                                  {family.phone} · {students.length} children
                                </small>
                              </div>
                            </div>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => closeFamily(false)}
                            >
                              Change family
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {family && students.length > 0 && (
                      <>
                        {/* ======================================================
                            STEP 2 - HOW MUCH DID THE PARENT GIVE
                        ====================================================== */}
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body">
                            <SectionTitle
                              no={2}
                              title="How much did the parent give?"
                              hint="Type the full amount once. It is divided between the children below."
                            />

                            <div className="row g-3">
                              <div className="col-12">
                                <label className="form-label fw-semibold">
                                  Total amount received{" "}
                                  <sup className="text-danger">*</sup>
                                </label>
                                <div
                                  className="input-group input-group-lg"
                                  style={{ maxWidth: 380 }}
                                >
                                  <span className="input-group-text">₹</span>
                                  <input
                                    type="text"
                                    inputMode="numeric"
                                    className="form-control fw-bold"
                                    placeholder="e.g. 50000"
                                    value={received}
                                    onChange={(e) =>
                                      onReceivedChange(
                                        e.target.value.replace(/\D/g, ""),
                                      )
                                    }
                                  />
                                </div>
                                <small className="text-muted">
                                  All children together have{" "}
                                  <b className="text-danger">
                                    ₹{inr(totalPending)}
                                  </b>{" "}
                                  pending.
                                </small>
                              </div>

                              <div className="col-lg-4 col-md-6">
                                <label className="form-label fw-semibold">
                                  Paid into which bank?{" "}
                                  <sup className="text-danger">*</sup>
                                </label>
                                <select
                                  className="form-select"
                                  value={form.bank_id}
                                  onChange={setF("bank_id")}
                                >
                                  <option value="">Select bank</option>
                                  {banks.map((b) => (
                                    <option key={b.id} value={b.id}>
                                      {b.account_no} ({b.owner_name})
                                    </option>
                                  ))}
                                </select>
                              </div>

                              <div className="col-lg-4 col-md-6">
                                <label className="form-label fw-semibold">
                                  Transaction no. (if online)
                                </label>
                                <input
                                  className="form-control"
                                  placeholder="Leave empty for cash"
                                  value={form.transaction_no}
                                  onChange={setF("transaction_no")}
                                />
                              </div>

                              <div className="col-lg-4 col-md-6">
                                <label className="form-label fw-semibold">
                                  Payment date
                                </label>
                                <input
                                  type="date"
                                  className="form-control"
                                  value={form.payment_date}
                                  onChange={setF("payment_date")}
                                />
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 mt-3 text-decoration-none"
                              onClick={() => setMore((m) => !m)}
                            >
                              {more ? "Hide" : "Show"} more options (next
                              payment date, remark, receipt number)
                            </button>

                            {more && (
                              <div className="row g-3 mt-1">
                                <div className="col-lg-4 col-md-6">
                                  <label className="form-label fw-semibold">
                                    Next payment date
                                  </label>
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={form.next_payment_date}
                                    onChange={setF("next_payment_date")}
                                  />
                                </div>

                                <div className="col-lg-4 col-md-6">
                                  <label className="form-label fw-semibold">
                                    First receipt no.
                                  </label>
                                  <div className="input-group">
                                    {receiptStart.prefix && (
                                      <span className="input-group-text fw-semibold">
                                        {receiptStart.prefix}
                                      </span>
                                    )}
                                    <input
                                      className="form-control fw-semibold"
                                      inputMode="numeric"
                                      value={receiptStart.number}
                                      onChange={(e) =>
                                        setReceiptStart((r) => ({
                                          ...r,
                                          number: e.target.value.replace(
                                            /\D/g,
                                            "",
                                          ),
                                        }))
                                      }
                                    />
                                  </div>
                                  {receiptMsg ? (
                                    <small className="text-danger">
                                      {receiptMsg}
                                    </small>
                                  ) : (
                                    <small className="text-muted">
                                      Next children get the following numbers.
                                    </small>
                                  )}
                                </div>

                                <div className="col-12">
                                  <label className="form-label fw-semibold">
                                    Remark
                                  </label>
                                  <textarea
                                    className="form-control input-uppercase"
                                    rows={2}
                                    value={form.remark}
                                    onChange={setF("remark")}
                                  />
                                </div>

                                {setting?.message_status === "Yes" && (
                                  <div className="col-12">
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="sendMessage"
                                        checked={form.sendMessage}
                                        onChange={(e) =>
                                          setForm((f) => ({
                                            ...f,
                                            sendMessage: e.target.checked,
                                          }))
                                        }
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor="sendMessage"
                                      >
                                        Send payment confirmation message
                                      </label>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* ======================================================
                            STEP 3 - DIVIDE BETWEEN CHILDREN
                        ====================================================== */}
                        <div className="card border-0 shadow-sm mb-3">
                          <div className="card-body">
                            <SectionTitle
                              no={3}
                              title="Divide between children"
                              hint="Every child who gets an amount gets a separate receipt."
                            />

                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 px-3 py-2 rounded-3 bg-label-primary">
                              <small className="fw-semibold">
                                {splitMode === "manual"
                                  ? "You are typing the amounts yourself."
                                  : "Amounts are filled automatically. You can type a different amount in any box."}
                              </small>
                              <div className="d-flex flex-wrap gap-2">
                                {splitMode === "manual" && (
                                  <button
                                    className="btn btn-sm btn-primary"
                                    disabled={!receivedAmt}
                                    onClick={() => setSplitMode("sequential")}
                                  >
                                    Fill automatically
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  disabled={!receivedAmt}
                                  onClick={() => setSplitMode("equal")}
                                >
                                  Divide equally
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  disabled={!allocated}
                                  onClick={clearAll}
                                >
                                  Clear all
                                </button>
                              </div>
                            </div>

                            <div className="d-flex flex-column gap-3">
                              {students.map((s, idx) => {
                                const hasDue = s.pending > 0;
                                const paying = s.payAmount > 0;
                                return (
                                  <div
                                    key={s.student_id}
                                    className={`border rounded-3 ${paying ? "border-success" : ""}`}
                                    style={{
                                      background: paying
                                        ? "rgba(113,221,55,.05)"
                                        : "",
                                    }}
                                  >
                                    <div className="p-3">
                                      <div className="row g-3 align-items-center">
                                        <div className="col-md-7">
                                          <div className="d-flex align-items-center gap-3">
                                            <div
                                              className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary fw-bold"
                                              style={{
                                                width: 44,
                                                height: 44,
                                                flexShrink: 0,
                                              }}
                                            >
                                              {initials(s.name)}
                                            </div>
                                            <div>
                                              <div className="fw-bold">
                                                {s.name}
                                              </div>
                                              <small className="text-muted">
                                                Reg. {s.registration_no} · Total
                                                ₹{inr(s.total)} · Paid ₹
                                                {inr(s.paid)}
                                              </small>
                                              <div>
                                                {hasDue ? (
                                                  <span className="fw-semibold text-danger">
                                                    Pending ₹{inr(s.pending)}
                                                  </span>
                                                ) : (
                                                  <span className="badge bg-label-success">
                                                    All fees paid
                                                  </span>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="col-md-5">
                                          {hasDue && (
                                            <>
                                              <label className="small fw-semibold text-muted mb-1">
                                                Pay now
                                              </label>
                                              <div className="d-flex gap-2">
                                                <div className="input-group">
                                                  <span className="input-group-text">
                                                    ₹
                                                  </span>
                                                  <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="form-control fw-bold"
                                                    placeholder="0"
                                                    value={s.payAmount || ""}
                                                    onChange={(e) =>
                                                      setStudentAmount(
                                                        idx,
                                                        e.target.value.replace(
                                                          /\D/g,
                                                          "",
                                                        ),
                                                      )
                                                    }
                                                  />
                                                </div>
                                                <button
                                                  className="btn btn-outline-secondary"
                                                  title="Pay full pending"
                                                  onClick={() => payFull(idx)}
                                                >
                                                  Full
                                                </button>
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      </div>

                                      {paying ? (
                                        <div className="mt-3 pt-3 border-top">
                                          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                            <span className="badge bg-primary py-2">
                                              Receipt {rno[s.student_id]}
                                            </span>
                                            <small className="text-muted">
                                              ₹{inr(s.payAmount)} will be added
                                              to:
                                            </small>
                                          </div>
                                          <div className="d-flex flex-wrap gap-2">
                                            {s.feeData
                                              .filter(
                                                (f) =>
                                                  f.selected && f.payAmount > 0,
                                              )
                                              .map((f) => (
                                                <span
                                                  key={f.name}
                                                  className="badge bg-label-primary fw-normal py-2"
                                                >
                                                  {f.name}: ₹{inr(f.payAmount)}
                                                </span>
                                              ))}
                                          </div>
                                        </div>
                                      ) : (
                                        hasDue && (
                                          <small className="text-muted d-block mt-2">
                                            No receipt for this child (amount is
                                            0).
                                          </small>
                                        )
                                      )}

                                      {hasDue && (
                                        <button
                                          className="btn btn-link btn-sm p-0 mt-2 text-decoration-none"
                                          onClick={() => toggleOpen(idx)}
                                        >
                                          {s.open
                                            ? "Close"
                                            : "Change fee types"}
                                        </button>
                                      )}
                                    </div>

                                    {s.open && (
                                      <div
                                        className="border-top p-3"
                                        style={{
                                          background: "rgba(0,0,0,.02)",
                                        }}
                                      >
                                        {s.batch_n_fees?.length > 1 && (
                                          <div
                                            className="mb-3"
                                            style={{ maxWidth: 280 }}
                                          >
                                            <label className="form-label small fw-semibold mb-1">
                                              Class / Batch
                                            </label>
                                            <select
                                              className="form-select form-select-sm"
                                              value={s.batchId}
                                              onChange={(e) =>
                                                changeBatch(idx, e.target.value)
                                              }
                                            >
                                              <option value="">All</option>
                                              {s.batch_n_fees.map((b) => (
                                                <option
                                                  key={b.id}
                                                  value={b.batch_id}
                                                >
                                                  {b.batch?.title}
                                                </option>
                                              ))}
                                            </select>
                                          </div>
                                        )}

                                        <div className="d-flex flex-column gap-2">
                                          {s.feeData
                                            .filter((f) => f.pending > 0)
                                            .map((f) => {
                                              const id = `fee-${s.student_id}-${f.name}`;
                                              return (
                                                <div
                                                  key={f.name}
                                                  className={`border rounded-3 px-3 py-2 ${f.selected ? "border-primary" : ""}`}
                                                  style={{
                                                    background: f.selected
                                                      ? "rgba(var(--bs-primary-rgb), .04)"
                                                      : "#fff",
                                                  }}
                                                >
                                                  <div className="row align-items-center g-2">
                                                    <div className="col-12 col-md-6">
                                                      <div className="form-check mb-0">
                                                        <input
                                                          className="form-check-input"
                                                          type="checkbox"
                                                          id={id}
                                                          checked={f.selected}
                                                          onChange={() =>
                                                            toggleFee(
                                                              idx,
                                                              f.name,
                                                            )
                                                          }
                                                        />
                                                        <label
                                                          className="form-check-label fw-semibold"
                                                          htmlFor={id}
                                                        >
                                                          {f.name}
                                                        </label>
                                                      </div>
                                                      <div className="ms-4 small text-muted">
                                                        Pending:{" "}
                                                        <span className="text-danger fw-semibold">
                                                          ₹{inr(f.pending)}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    <div className="col-12 col-md-6">
                                                      <div className="input-group input-group-sm">
                                                        <span className="input-group-text">
                                                          ₹
                                                        </span>
                                                        <input
                                                          type="text"
                                                          inputMode="numeric"
                                                          className="form-control fw-semibold"
                                                          placeholder="0"
                                                          value={
                                                            f.payAmount || ""
                                                          }
                                                          disabled={!f.selected}
                                                          readOnly={
                                                            f.name ===
                                                            "Registration Fee"
                                                          }
                                                          onChange={(e) =>
                                                            setFeeAmount(
                                                              idx,
                                                              f.name,
                                                              e.target.value.replace(
                                                                /\D/g,
                                                                "",
                                                              ),
                                                            )
                                                          }
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              );
                                            })}
                                        </div>
                                        <small className="text-muted d-block mt-2">
                                          Money goes to the oldest fees first.
                                          Untick a fee to skip it. Registration
                                          Fee is paid fully together with
                                          Tuition Fee.
                                        </small>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* ======================================================
                            SAVE BAR
                        ====================================================== */}
                        <div
                          className="card border-0 shadow"
                          style={{ position: "sticky", bottom: 12, zIndex: 10 }}
                        >
                          <div className="card-body py-3">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                              <div className="d-flex flex-wrap gap-4">
                                <div>
                                  <small className="text-muted d-block">
                                    Parent gave
                                  </small>
                                  <span className="fs-5 fw-bold text-primary">
                                    ₹{inr(receivedAmt)}
                                  </span>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    Divided
                                  </small>
                                  <span className="fs-5 fw-bold text-success">
                                    ₹{inr(allocated)}
                                  </span>
                                </div>
                                <div>
                                  <small className="text-muted d-block">
                                    {remaining < 0 ? "Too much" : "Left"}
                                  </small>
                                  <span
                                    className={`fs-5 fw-bold ${
                                      remaining === 0 && receivedAmt
                                        ? "text-success"
                                        : remaining < 0
                                          ? "text-danger"
                                          : "text-warning"
                                    }`}
                                  >
                                    ₹{inr(Math.abs(remaining))}
                                  </span>
                                </div>
                              </div>

                              <div className="d-flex align-items-center gap-3">
                                <small
                                  className={
                                    blocker
                                      ? "text-muted"
                                      : "text-success fw-semibold"
                                  }
                                >
                                  {blocker ||
                                    `Ready. ${receipts.length} separate ${receipts.length === 1 ? "receipt" : "receipts"} will be created.`}
                                </small>
                                <button
                                  className="btn btn-primary px-4"
                                  disabled={!!blocker}
                                  onClick={handleSave}
                                >
                                  <i className="ri-check-line me-1" />
                                  Save{" "}
                                  {receipts.length > 1
                                    ? `${receipts.length} receipts`
                                    : "payment"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}

                    {family && !loading && students.length === 0 && (
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5 text-muted">
                          No children found for this family.
                        </div>
                      </div>
                    )}

                    {!family && (
                      <div className="card border-0 shadow-sm">
                        <div className="card-body text-center py-5 text-muted">
                          <i
                            className="ri-group-line"
                            style={{ fontSize: 44 }}
                          />
                          <h5 className="mt-2">Search a family to begin</h5>
                          <div
                            className="mx-auto text-start mt-3"
                            style={{ maxWidth: 420 }}
                          >
                            <div className="mb-1">
                              1. Search and open the family.
                            </div>
                            <div className="mb-1">
                              2. Type the total amount the parent gave.
                            </div>
                            <div>
                              3. Check how it is divided between children, then
                              save. Each child gets their own receipt.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="content-backdrop fade" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
